import React, { type JSX, useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import { Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';

import { SearchReferralsDocument } from '../graphql/queries/__generated__/SearchReferrals';
import {
  CustomerRowData,
  getCustomerRowData,
  groupByMilestone,
  MILESTONE_LABEL,
} from '../helpers/customerRowData';
import { ContentCard } from './ContentCard';
import { ContentWrapper } from './ContentWrapper';
import CustomerRow from './CustomerRow';
import LadyAtDeskIcon from './icons/LadyAtDeskIcon';
import { Loader } from './Loader';

const MAX_SELLER_LEAD_DOCUMENTS = 1000;

const CustomersSection: React.FC = () => {
  const { data, loading } = useQuery(SearchReferralsDocument, {
    variables: { input: { size: MAX_SELLER_LEAD_DOCUMENTS } },
  });

  const groups = useMemo(() => {
    const rows =
      data?.searchReferrals.referrals
        .map(getCustomerRowData)
        .filter((r): r is CustomerRowData => r !== null) ?? [];
    return groupByMilestone(rows);
  }, [data]);

  const totalCount = groups.reduce((acc, g) => acc + g.rows.length, 0);

  let content: JSX.Element;

  if (loading) {
    content = <Loader size="small" />;
  } else if (totalCount > 0) {
    content = (
      <>
        {groups.map(({ milestone, rows }) => (
          <Col key={milestone} className="pt-4">
            <Row className="items-baseline pb-2">
              <Text as="h3" className="text-label-md font-medium">
                {MILESTONE_LABEL[milestone]}
              </Text>
              <Text as="span" className="text-body-sm pl-1.5 text-od-content-secondary">
                {rows.length}
              </Text>
            </Row>
            {rows.map((row, idx) => (
              <Col
                key={row.referralUuid}
                className={cn(
                  'py-4',
                  idx !== rows.length - 1 && 'border-b border-od-border-secondary',
                )}
              >
                <CustomerRow data={row} />
              </Col>
            ))}
          </Col>
        ))}
      </>
    );
  } else {
    content = (
      <>
        <div style={{ height: '30px' }} />
        <Col className="items-center pt-10">
          <LadyAtDeskIcon />
        </Col>
        <Text as="p" className="text-label-lg py-2 text-center">
          No active customers
        </Text>
        <Col className="w-4/5 self-center pb-10">
          <Text as="p" className="text-body-sm text-center">
            Customers who request an offer from Opendoor using your referral link will appear here
            so that you can support them through the process.
          </Text>
        </Col>
        <div style={{ height: '150px' }} />
      </>
    );
  }

  const title =
    totalCount > 0 ? `Your Opendoor Customers (${totalCount})` : 'Your Opendoor Customers';

  return (
    <ContentWrapper>
      <ContentCard title={title}>{content}</ContentCard>
    </ContentWrapper>
  );
};

export default CustomersSection;
